import type { Idioma } from '../i18n';
import { capturarEscopoMfa, notificarMfa } from './mfa/ponte';
/** Cliente HTTP único. Cookie de sessão vai junto (mesma origem); erro chega como ErroApi. */
export class ErroApi extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: Record<string, unknown>,
    /** lista completa de erros de domínio ({ code, message, details }) quando a API devolve várias */
    readonly erros: { code: string; message: string; details?: Record<string, unknown> }[] = [],
    readonly retryAfter?: number,
    readonly causa?: 'tempo_esgotado',
  ) {
    super(message);
  }
}

export async function api<T>(caminho: string, init: RequestInit & { json?: unknown } = {}): Promise<T> {
  const { json, headers, signal, ...resto } = init;
  const escopoMfa = capturarEscopoMfa();
  let r: Response;
  const sinal = signal ? AbortSignal.any([signal, AbortSignal.timeout(30_000)]) : AbortSignal.timeout(30_000);
  try {
    // ROB-R4-02: sem resposta em 30 s a tela sai do "Carregando" sem repetir o timeout.
    r = await fetch(caminho, {
      credentials: 'same-origin',
      ...resto,
      signal: sinal,
      headers: { ...(json !== undefined ? { 'content-type': 'application/json' } : {}), ...headers },
      body: json !== undefined ? JSON.stringify(json) : init.body,
    });
  } catch {
    if (sinal.aborted) {
      if (sinal.reason?.name === 'TimeoutError' && !(signal?.aborted && sinal.reason === signal.reason)) {
        throw new ErroApi(0, 'rede', 'rede', undefined, [], undefined, 'tempo_esgotado');
      }
    }
    throw new ErroApi(0, 'rede', 'rede');
  }
  if (r.status === 204) return undefined as T;
  const corpo = (await r.json().catch(() => ({}))) as { code?: string; message?: string; details?: Record<string, unknown>; errors?: ErroApi['erros'] };
  if (!r.ok) {
    notificarMfa(caminho, r.status, corpo.code ?? 'erro', corpo.details, escopoMfa);
    const cabecalho = r.headers.get('Retry-After');
    const segundos = cabecalho !== null && /^\d+$/.test(cabecalho.trim()) ? Number(cabecalho) : NaN;
    const retryAfter = r.status === 503 && corpo.code === 'servico_indisponivel' && Number.isSafeInteger(segundos)
      ? segundos : undefined;
    throw new ErroApi(r.status, corpo.code ?? 'erro', corpo.message ?? r.statusText, corpo.details, corpo.errors ?? [], retryAfter);
  }
  return corpo as T;
}

export interface Eu {
  usuario: { id: string; nome: string; email: string; idioma: 'pt' | 'en' | 'es'; tema: 'escuro' | 'claro'; papel: string; onboardingConcluido: boolean };
  tenant_id: string;
  workspace_id: string;
  permissoes: string[];
  workspaces: { tenant_id: string; tenant_nome: string; workspace_id: string; workspace_nome: string; slug: string; sandbox: boolean }[];
  modulos?: string[];
  aparencia?: { cor: string | null; tema: 'escuro' | 'claro' | null; idioma: Idioma | null };
  /** M12.4: vazio = todas as empresas do grupo */
  empresas_permitidas?: string[];
  papel_fabrica?: string | null;
  /** SPEC2-01: o usuário já escolheu idioma ou tema (a escolha vence o padrão do workspace). */
  preferencias_definidas?: boolean;
}
